import React, { useState } from "react";
import { X, Sparkles } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/** Auto-create batches from the unassigned raw pool. */
export default function AutoBatchModal({ onClose, onCreated }) {
  const [bySubject, setBySubject] = useState(true);
  const [byCount, setByCount] = useState(false);
  const [count, setCount] = useState(50);
  const [namePrefix, setNamePrefix] = useState("Batch");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!bySubject && !byCount) {
      toast.error("Pick at least one strategy");
      return;
    }
    if (byCount && (!count || count < 1)) {
      toast.error("Questions per batch must be at least 1");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/batches/auto-create", {
        by_subject: bySubject,
        by_count: byCount,
        count_per_batch: byCount ? Number(count) : null,
        name_prefix: namePrefix || "Batch",
      });
      toast.success(`Created ${data.created} batch${data.created === 1 ? "" : "es"}`);
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
      <div data-testid="auto-batch-modal" className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </div>
            <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
              Auto-Create Batches
            </h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-slate-500 mb-5">
          Split the unassigned raw pool into reviewer batches. You can group by subject, by a fixed
          batch size, or both.
        </p>

        <div className="space-y-3">
          <label
            className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer"
            data-testid="auto-batch-by-subject-row"
          >
            <input
              type="checkbox"
              checked={bySubject}
              onChange={(e) => setBySubject(e.target.checked)}
              className="mt-0.5 rounded border-slate-300 text-orange-500 focus:ring-orange-500/20"
            />
            <div>
              <div className="text-sm font-semibold text-slate-900">Group by Subject</div>
              <div className="text-xs text-slate-500">
                One batch per subject (Pharmacology, Anatomy, …).
              </div>
            </div>
          </label>

          <label
            className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer"
            data-testid="auto-batch-by-count-row"
          >
            <input
              type="checkbox"
              checked={byCount}
              onChange={(e) => setByCount(e.target.checked)}
              className="mt-0.5 rounded border-slate-300 text-orange-500 focus:ring-orange-500/20"
            />
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-900">Split by Question Count</div>
              <div className="text-xs text-slate-500 mb-2">
                Chunk into batches of this many questions.
              </div>
              <input
                type="number"
                min={1}
                max={1000}
                value={count}
                onChange={(e) => setCount(e.target.value)}
                disabled={!byCount}
                data-testid="auto-batch-count-input"
                className="w-28 px-3 py-1.5 rounded-lg border border-slate-200 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none disabled:bg-slate-50 disabled:text-slate-400"
              />
              <span className="text-xs text-slate-500 ml-2">per batch</span>
            </div>
          </label>
        </div>

        <div className="mt-5">
          <label className="block text-xs font-mono uppercase tracking-wider text-slate-500 mb-1.5">
            Name Prefix
          </label>
          <input
            value={namePrefix}
            onChange={(e) => setNamePrefix(e.target.value)}
            data-testid="auto-batch-name-prefix"
            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
            placeholder="Batch"
          />
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100">
            Cancel
          </button>
          <button
            data-testid="auto-batch-create-btn"
            onClick={submit}
            disabled={busy || (!bySubject && !byCount)}
            className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:bg-slate-200 disabled:text-slate-400 text-white text-sm font-semibold"
          >
            {busy ? "Creating…" : "Create Batches"}
          </button>
        </div>
      </div>
    </div>
  );
}
